import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableColumnDefinition,
  TableColumnId,
  TableColumnSizingOptions,
  TableRowId,
  makeStyles,
} from "@fluentui/react-components";
import { useSearchParams } from "react-router-dom";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { UserInformation } from "../../models/UserInformation";
import { SkanskaColorTokens } from "../../style/colors";
import { Breakpoints } from "../../style/styles";
import { IQuickEditableEntity } from "./GenericReactGrid";

export interface IItemMetadata extends IQuickEditableEntity {
  id: string;
  title: string;
  createdTimeInUTC?: string;
  modifiedTimeInUTC?: string;
  createdBy?: UserInformation;
  modifiedBy?: UserInformation;
  versionId: string;
  mirroredActivityId?: string;
  draftCreatedBy?: UserInformation;
}

declare type SortDirection = "ascending" | "descending";

declare interface SortState {
  sortColumn: TableColumnId | undefined;
  sortDirection: SortDirection;
}

interface GenericDataGridProps<T extends IItemMetadata> {
  items: IItemMetadata[];
  columns?: VsaTableColumnDefinition<T>[];
  getRowId: (item: T) => TableRowId;
  enableSelection?: boolean;
  onSelectionChange?: (newSelections: TableRowId[]) => void;
  columnSizingOptions?: TableColumnSizingOptions;
  onSortChange?: (column?: string, ascending?: boolean) => void;
  onColumnResize?: (columnId: string, width: number) => void;
  defaultSelectedItems?: Set<TableRowId>;
}

const useStyle = makeStyles({
  gridWrapper: {
    width: "100%",
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    paddingRight: "0",
    overflowX: "auto",
    overflowY: "auto",
    boxSizing: "border-box",
    ...Breakpoints.small({ maxHeight: "350px" }),
    ...Breakpoints.medium({ maxHeight: "400px" }),
    ...Breakpoints.large({ maxHeight: "425px" }),
    ...Breakpoints.extraLarge({ maxHeight: "800px" }),
    ...Breakpoints.extraExtraLarge({ maxHeight: "calc(100vh - 450px)" }),
  },
  stickyHeaderCell: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: SkanskaColorTokens.color_secondary_white,
  },
  topAlignedCell: {
    alignItems: "flex-start",
    padding: "8px 0px",
  },
});

const GenericDataGrid = <T extends IItemMetadata>(
  props: GenericDataGridProps<T>
) => {
  const {
    items,
    columns,
    getRowId,
    enableSelection,
    onSelectionChange,
    columnSizingOptions,
    onSortChange,
    onColumnResize,
    defaultSelectedItems,
  } = props;
  const classes = useStyle();

  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get("sortBy");
  const ascending = searchParams.get("sortDir");

  const defaultSortState: SortState = sortBy
    ? {
        sortColumn: sortBy,
        sortDirection: ascending === "asc" ? "ascending" : "descending",
      }
    : {
        sortColumn: "modified",
        sortDirection: "descending",
      };

  const handleSortChange = (sortState?: SortState | undefined) => {
    if (!onSortChange) {
      return;
    }

    const column = sortState?.sortColumn as string;

    if (sortState && column) {
      searchParams.set("sortBy", column);
      searchParams.set(
        "sortDir",
        sortState.sortDirection === "descending" ? "desc" : "asc"
      );
    } else {
      searchParams.delete("sortBy");
      searchParams.delete("sortDir");
    }

    setSearchParams(searchParams, {
      preventScrollReset: true,
    });

    onSortChange(column, sortState?.sortDirection === "ascending");
  };

  const handleSelectionChange =
    onSelectionChange || enableSelection
      ? (
          _e: unknown,
          data: { selectedItems: Set<TableRowId> | TableRowId[] }
        ) => {
          if (!onSelectionChange) {
            return;
          }

          const selectedItems = data.selectedItems;

          if (selectedItems instanceof Set) {
            onSelectionChange(Array.from(selectedItems));
            return;
          }

          if (Array.isArray(selectedItems)) {
            onSelectionChange(selectedItems as TableRowId[]);
            return;
          }

          onSelectionChange([]);
        }
      : undefined;

  return (
    <div className={classes.gridWrapper}>
      <DataGrid
        items={items}
        // The casting here is a workaround for the FAULTY TableColumnDefinition which mandates a compare function which in turn makes it impossible to make columns non sortable.
        columns={columns ? (columns as TableColumnDefinition<T>[]) : []}
        sortable
        resizableColumns
        resizableColumnsOptions={{ autoFitColumns: false }}
        columnSizingOptions={columnSizingOptions}
        onColumnResize={
          onColumnResize
            ? (_e, data) => onColumnResize(String(data.columnId), data.width)
            : undefined
        }
        defaultSortState={defaultSortState}
        onSortChange={(_a, state) => handleSortChange(state)}
        getRowId={getRowId}
        focusMode="composite"
        selectionMode={
          enableSelection || onSelectionChange ? "multiselect" : undefined
        }
        defaultSelectedItems={defaultSelectedItems}
        onSelectionChange={handleSelectionChange}
      >
        <DataGridHeader className={classes.stickyHeaderCell}>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<T>>
          {({ item, rowId }) => (
            <DataGridRow<T> key={rowId} className={classes.topAlignedCell}>
              {({ renderCell }) => (
                <DataGridCell>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );
};

export default GenericDataGrid;
